/**
 * Criterios y bbox de `POST /filterofficialunits`.
 *
 * Los criterios se arman aparte del bbox para poder cachear una respuesta y
 * reusarla mientras el usuario panea dentro del área ya pedida.
 *
 * @see docs/maps/official-unit-filters.md
 */

import {
  countActiveOfficialUnitFilters,
  getOfficialUnitFilterDefinitions,
  isFilterValueActive,
} from '../data/parcelFilterContract';
import { OFFICIAL_UNIT_FILTER_MIN_ZOOM } from '../styles/mapTheme';

/**
 * @param {Array<{ min?: number|null, max?: number|null, value: string }>} options
 * @param {string[]} selectedValues
 * @returns {{ min?: number, max?: number }|null}
 */
export function collapseSelectedRangeBands(options, selectedValues) {
  const selected = (options || []).filter((opt) => selectedValues.includes(opt.value));
  if (!selected.length) return null;

  const openMin = selected.some((opt) => opt.min == null);
  const openMax = selected.some((opt) => opt.max == null);
  const mins = selected.map((opt) => opt.min).filter((value) => value != null);
  const maxs = selected.map((opt) => opt.max).filter((value) => value != null);

  const range = {};
  if (!openMin && mins.length) range.min = Math.min(...mins);
  if (!openMax && maxs.length) range.max = Math.max(...maxs);
  if (range.min == null && range.max == null) return null;
  return range;
}

/**
 * Criterios catastrales activos, sin bbox. Las bandas del panel se colapsan a
 * un solo `{ min?, max? }`: si el usuario elige bandas no contiguas, el rango
 * cubre el hueco porque el backend solo acepta un intervalo.
 *
 * @param {Record<string, unknown>} filters
 * @returns {object|null} `null` si no hay ningún criterio catastral activo.
 */
export function buildOfficialUnitFilterCriteria(filters) {
  if (countActiveOfficialUnitFilters(filters) === 0) return null;

  const criteria = {};
  getOfficialUnitFilterDefinitions().forEach((def) => {
    const value = filters?.[def.field];
    if (!isFilterValueActive(value, def)) return;

    if (def.component === 'textContains') {
      criteria.q = String(value).trim();
      return;
    }
    if (def.component === 'rangeBands' && def.dbField) {
      const range = collapseSelectedRangeBands(def.options, value);
      if (range) criteria[def.dbField] = range;
    }
  });

  return Object.keys(criteria).length ? criteria : null;
}

/**
 * Clave estable de criterios + provincia. Identifica a qué consulta pertenece
 * una respuesta cacheada.
 *
 * @param {object|null} criteria
 * @param {string|null} [province]
 * @returns {string|null}
 */
export function officialUnitCriteriaKey(criteria, province = null) {
  if (!criteria) return null;
  const ordered = Object.keys(criteria).sort().map((key) => [key, criteria[key]]);
  return JSON.stringify([String(province || ''), ordered]);
}

/**
 * @param {object} payload
 * @param {number[]} payload.bbox
 * @param {object} payload.criteria
 * @param {string|null} [payload.province]
 * @returns {{ bbox: number[], province?: string, filters: object }}
 */
export function buildOfficialUnitFilterBody({ bbox, criteria, province = null }) {
  const body = { bbox, filters: criteria };
  const provinceName = String(province || '').trim();
  if (provinceName) body.province = provinceName;
  return body;
}

/**
 * @param {object} map - Instancia MapLibre.
 * @returns {number[]|null} `[west, south, east, north]`.
 */
export function readMapBbox(map) {
  if (!map || typeof map.getBounds !== 'function') return null;
  try {
    const bounds = map.getBounds();
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
    if (bbox.some((value) => !Number.isFinite(value))) return null;
    return bbox;
  } catch {
    return null;
  }
}

/**
 * @param {object} map - Instancia MapLibre.
 * @returns {number} Zoom actual, 0 si el mapa todavía no responde.
 */
export function readMapZoom(map) {
  try {
    const zoom = map?.getZoom?.();
    return Number.isFinite(zoom) ? zoom : 0;
  } catch {
    return 0;
  }
}

/**
 * Ensancha el bbox para que un paneo corto siga cayendo dentro de lo ya pedido.
 * La latitud se recorta a ±85 (límite de Web Mercator).
 *
 * @param {number[]} bbox - `[west, south, east, north]`.
 * @param {number} ratio - Proporción de ancho/alto a sumar de cada lado.
 * @returns {number[]}
 */
export function padBbox(bbox, ratio) {
  const [west, south, east, north] = bbox;
  const padX = (east - west) * ratio;
  const padY = (north - south) * ratio;
  return [
    west - padX,
    Math.max(-85, south - padY),
    east + padX,
    Math.min(85, north + padY),
  ];
}

/**
 * @param {number[]|null} outer
 * @param {number[]|null} inner
 * @returns {boolean} `true` si `inner` cae completamente dentro de `outer`.
 */
export function bboxContains(outer, inner) {
  if (!outer || !inner) return false;
  return outer[0] <= inner[0]
    && outer[1] <= inner[1]
    && outer[2] >= inner[2]
    && outer[3] >= inner[3];
}

/**
 * Búsqueda lo bastante específica como para pedir toda Córdoba y encuadrar
 * el lote: nomenclatura (~16) o cuenta (~12). Un prefijo corto sigue recortando
 * solo la vista.
 */
export const OFFICIAL_UNIT_LOCATOR_Q_MIN_LENGTH = 8;

/**
 * @param {object|null} criteria
 * @returns {boolean}
 */
export function isOfficialUnitLocatorQuery(criteria) {
  return String(criteria?.q || '').trim().length >= OFFICIAL_UNIT_LOCATOR_Q_MIN_LENGTH;
}

/**
 * Solo nomenclatura/cuenta, sin bandas. El usuario busca un padrón, no recorta
 * la vista: el aviso de zoom no aplica.
 *
 * @param {object|null} criteria
 * @returns {boolean}
 */
export function isOfficialUnitNomenclatureOnlyQuery(criteria) {
  const q = String(criteria?.q || '').trim();
  if (!q) return false;
  return Object.keys(criteria).length === 1;
}

/**
 * @param {object} [options]
 * @param {boolean} [options.inMap]
 * @returns {string}
 */
export function formatOfficialUnitZoomHint({ inMap = false } = {}) {
  const where = inMap ? ' en el mapa' : '';
  return `Acercá el zoom a ${OFFICIAL_UNIT_FILTER_MIN_ZOOM} o más para filtrar unidades${where}.`;
}
